import { Capacitor } from "@capacitor/core";
import type { CrashlyticsPort, CrashStackFrame } from "@yjsgames/crashlytics-ports";
import { NoopCrashlyticsAdapter } from "@yjsgames/crashlytics-ports";

/**
 * CrashlyticsPort 결정(DIP 플랫폼 분기 팩토리, `createGpgs`·`createAccountLinkProvider` 동형).
 * - 네이티브(Android/iOS): 버퍼링 facade — `import("./CapacitorCrashlyticsAdapter.js")`를 동적 로드하고,
 *   로드 전 호출된 record/log/key를 큐에 쌓아 로드 완료 시 순서대로 flush한다(부팅 초기 에러 유실 방지 —
 *   `account/createAccountLinkProvider`의 nativeFacade 선례). 로드 직후 `setEnabled(true)` 1회(live+dev 수집).
 * - 웹: 즉시 Noop(Crashlytics 웹 SDK 부재).
 */
export function createCrashlytics(): CrashlyticsPort {
  if (!Capacitor.isNativePlatform()) return new NoopCrashlyticsAdapter();
  return createBufferingCrashlytics(() =>
    import("./CapacitorCrashlyticsAdapter.js").then(({ CapacitorCrashlyticsAdapter }) => {
      const adapter = new CapacitorCrashlyticsAdapter();
      adapter.setEnabled(true); // live+dev native 모두 수집
      return adapter;
    }),
  );
}

/**
 * 버퍼링 facade — 어댑터 로드 전 호출을 큐에 쌓았다가 로드 완료 시 순서대로 위임한다. Capacitor 무의존(순수)이라
 * 단위테스트에서 fake loader/adapter로 버퍼링·flush 순서를 검증한다. 큐는 부팅 윈도(수백 ms)에만 채워지므로
 * 무한 증가 위험 없음.
 *
 * @param load 어댑터를 로드하는 Promise 팩토리(네이티브는 dynamic import, 테스트는 fake).
 * @param onReady 로드 완료 통지(선택, 테스트 훅).
 */
export function createBufferingCrashlytics(
  load: () => Promise<CrashlyticsPort>,
  onReady?: (port: CrashlyticsPort) => void,
): CrashlyticsPort {
  let active: CrashlyticsPort | null = null;
  const queue: ((p: CrashlyticsPort) => void)[] = [];
  const run = (fn: (p: CrashlyticsPort) => void): void => {
    if (active) fn(active);
    else queue.push(fn);
  };
  const flush = (adapter: CrashlyticsPort): void => {
    active = adapter;
    for (const fn of queue) fn(adapter);
    queue.length = 0;
    onReady?.(adapter);
  };
  // ⚠ load reject(번들/네트워크 오류) 시 큐가 영구 보류되지 않도록 Noop으로 강등 + flush — 부팅 시 쌓인
  //   호출을 소비(Noop이라 무동작)해 누수를 차단한다(Port non-throw 계약 유지).
  void load().then(flush, () => flush(new NoopCrashlyticsAdapter()));
  return {
    isAvailable: () => true,
    recordException: (m: string, o?: { stacktrace?: CrashStackFrame[] }) =>
      run((p) => p.recordException(m, o)),
    log: (m: string) => run((p) => p.log(m)),
    setUserId: (id: string) => run((p) => p.setUserId(id)),
    setCustomKey: (k: string, v: string | number | boolean) => run((p) => p.setCustomKey(k, v)),
    setEnabled: (e: boolean) => run((p) => p.setEnabled(e)),
  };
}
